import React, { useState } from 'react';
import {
  TextField, Button, Typography, Stack, Alert, Link,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import AuthShell from '../../components/auth/AuthShell';
import {
  validateName, validatePhone, validateEmail, validatePassword,
  runValidators, PHONE_RULE_KEY,
} from '../../utils/validation';
import PasswordRequirements from '../../components/PasswordRequirements';
import PasswordField from '../../components/common/PasswordField';
import { useResendTimer, formatMMSS } from '../../hooks/useResendTimer';

export default function Register() {
  const { t } = useTranslation();
  const { remaining, start: startResend } = useResendTimer();
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // OTP
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpDevCode, setOtpDevCode] = useState('');
  const [code, setCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);

  const set = (k) => (e) => {
    setForm((p) => ({ ...p, [k]: e.target.value }));
    setFieldErrors((p) => ({ ...p, [k]: '' })); // прибрати помилку поля при редагуванні
  };

  // Крок 1: валідація + надсилання коду
  const handleStart = async (e) => {
    e.preventDefault();
    setError('');
    const errs = runValidators({
      name: validateName(form.name),
      phone: validatePhone(form.phone),
      email: validateEmail(form.email), // опційний
      password: validatePassword(form.password),
      confirm: form.password !== form.confirm ? 'validation.passwordsMismatch' : '',
    });
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError(t('common.checkFields'));
      return;
    }
    setLoading(true);
    try {
      // Бекенд перевіряє, що номер ще НЕ зареєстрований, ДО надсилання SMS (намір 'register')
      const res = await api.requestOtp(form.phone, 'register');
      setOtpDevCode(res.devCode || '');
      setCode('');
      setOtpError('');
      setOtpOpen(true);
      startResend(res.retryAfter || 120);
    } catch (err) {
      setError(err.status === 409 ? t('auth.alreadyRegistered') : (err.message || t('auth.couldNotSendCode')));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (remaining > 0) return; // кулдаун ще триває
    setOtpError('');
    try {
      const res = await api.requestOtp(form.phone);
      setOtpDevCode(res.devCode || '');
      startResend(res.retryAfter || 120);
    } catch (err) {
      setOtpError(err.message || t('auth.couldNotSendCode'));
    }
  };

  // Крок 2: підтвердити код -> зареєструвати
  const handleVerify = async () => {
    setOtpError('');
    if (!code || code.length < 4) {
      setOtpError(t('auth.enterSmsCode'));
      return;
    }
    setOtpBusy(true);
    try {
      const v = await api.verifyOtp({ phone: form.phone, code });
      await api.register({
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        password: form.password,
        phoneToken: v.phoneToken,
      });
      // Повне перезавантаження, щоб Layout підхопив користувача
      window.location.assign('/user/cabinet');
    } catch (err) {
      setOtpError(err.message || t('auth.couldNotFinish'));
    } finally {
      setOtpBusy(false);
    }
  };

  return (
    <>
      <AuthShell title={t('auth.registerTitle')} subtitle={t('auth.registerSubtitle')}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleStart}>
          <Stack spacing={2}>
            <TextField label={t('common.name')} value={form.name} onChange={set('name')} fullWidth
              error={!!fieldErrors.name} helperText={fieldErrors.name ? t(fieldErrors.name) : ' '} />
            <TextField label={t('common.phone')} value={form.phone} onChange={set('phone')} fullWidth
              placeholder="+48601234567"
              error={!!fieldErrors.phone} helperText={t(fieldErrors.phone || PHONE_RULE_KEY)} />
            <TextField label={t('common.emailOptional')} type="email" value={form.email} onChange={set('email')} fullWidth
              error={!!fieldErrors.email} helperText={fieldErrors.email ? t(fieldErrors.email) : ' '} />
            <div>
              <PasswordField label={t('common.password')} value={form.password} onChange={set('password')} fullWidth
                error={!!fieldErrors.password} />
              <PasswordRequirements value={form.password} sx={{ mt: 1, px: 0.5 }} />
            </div>
            <PasswordField label={t('common.passwordConfirm')} value={form.confirm} onChange={set('confirm')} fullWidth
              error={!!fieldErrors.confirm} helperText={fieldErrors.confirm ? t(fieldErrors.confirm) : ' '} />
            <Button type="submit" variant="contained" disabled={loading} fullWidth>
              {loading ? t('auth.sendingCode') : t('auth.registerBtn')}
            </Button>
          </Stack>
        </form>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t('legal.consentNote')}{' '}
          <Link component={RouterLink} to="/terms">{t('legal.terms')}</Link>{' · '}
          <Link component={RouterLink} to="/privacy">{t('legal.privacy')}</Link>
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t('auth.haveAccount')} <Link component={RouterLink} to="/login">{t('auth.signIn')}</Link>
        </Typography>
      </AuthShell>

      {/* --- OTP --- */}
      <Dialog open={otpOpen} onClose={() => !otpBusy && setOtpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('auth.otpTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('auth.otpSentTo', { phone: form.phone })}
          </Typography>
          {otpDevCode && (
            <Alert severity="warning" sx={{ mb: 2 }}>{t('auth.devCode', { code: otpDevCode })}</Alert>
          )}
          <TextField
            label={t('auth.smsCode')}
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric' }}
            autoFocus
            sx={{ mb: 1 }}
          />
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={handleResend}
            disabled={otpBusy || remaining > 0}
            sx={{ color: remaining > 0 ? 'text.disabled' : undefined }}
          >
            {remaining > 0 ? t('auth.resendIn', { time: formatMMSS(remaining) }) : t('auth.resend')}
          </Link>
          {otpError && <Alert severity="error" sx={{ mt: 2 }}>{otpError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOtpOpen(false)} disabled={otpBusy}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleVerify} disabled={otpBusy}>
            {otpBusy ? t('auth.registering') : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
